import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concat, EMPTY, filter, from, interval, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, take } from 'rxjs/operators';

import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { NinetyNotification } from '@ninety/platform/models/notifications/ninety-notification';
import {
  itemTypeToDetailType,
  itemTypeToPageRoute,
  NotificationItemType,
} from '@ninety/platform/models/notifications/notification-item-type';
import {
  DetailViewActions,
  TeamListStateActions,
  TeamSelectors,
  UserModelState,
  NotificationActions,
  OneSignalActions,
  appActions,
  UsersStateActions,
  selectCurrentUser,
  selectCurrentUserId,
} from '@ninety/platform/state';
import { selectNotificationsEnabled } from '@ninety/platform/state/app-global/notifications/notification.selectors';
import { LoggingActions } from '@ninety/platform/state/app-logging/app-logging.actions';

import { NotificationFeedService } from '../services/notification-feed.service';

import { NotificationFeedActions } from './notification-feed.actions';
import { NotificationFeedSelectors } from './notification-feed.selectors';

@Injectable()
export class NotificationFeedEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationFeedService = inject(NotificationFeedService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  /** FullAppInit flags (incl. the feed flag) are in the store before fetchTier2Data, so no LD trigger is needed.
   * Don't init on LD pushes: LD streams every flag edit to every open tab, so every tab would call /init
   * at the same moment and take down ninety-notifications. */
  checkForNewNotificationsAndShowBadge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.fetchTier2Data, NotificationFeedActions.init),
      concatLatestFrom(() => [this.store.select(selectCurrentUser), this.store.select(selectNotificationsEnabled)]),
      filter(([, , notificationFeedEnabled]) => !!notificationFeedEnabled),
      switchMap(([_, user]) =>
        this.notificationFeedService.getNotificationsInit().pipe(
          map(resp => {
            const unreadStatusIndicator = this.computeUnreadStatusIndicator(resp.notifications, user);
            return NotificationFeedActions.getUserNotificationsSuccess({ resp, unreadStatusIndicator });
          }),
          catchError((error: unknown) => of(NotificationFeedActions.getUserNotificationsFailure({ error })))
        )
      )
    )
  );

  getUserNotificationsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.getUserNotificationsFailure),
      map(({ error }) =>
        LoggingActions.error({
          log: {
            message: 'Failed to initialize notification feed',
            error,
          },
        })
      )
    )
  );

  /** Right now, clicking on a notification closes the feed and takes you to the item.
   *  There is no back button. You have to just open the feed again.
   *  Therefore, we will use the previously set filter so that the user can "go back" to the previous state */
  getPageOneFilteredNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open, NotificationFeedActions.setFilterOption),
      concatLatestFrom(() => this.store.select(NotificationFeedSelectors.selectFilterBy)),
      switchMap(([_, filterBy]) =>
        this.notificationFeedService.getNotifications(filterBy).pipe(
          map(resp => NotificationFeedActions.getUserNotificationsSuccess({ resp, unreadStatusIndicator: false })),
          catchError((error: unknown) => of(NotificationFeedActions.getUserNotificationsFailure({ error })))
        )
      )
    )
  );

  /** only for infinite scroll in the feed, only fires if state.hasMore is true and scrolled near bottom of feed */
  loadMoreNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.loadMoreNotifications),
      concatLatestFrom(() => this.store.select(NotificationFeedSelectors.selectPageAndFilter)),
      switchMap(([, { filterBy, currentPage }]) =>
        this.notificationFeedService.getNotifications(filterBy, currentPage + 1).pipe(
          map(resp => NotificationFeedActions.loadMoreNotificationsSuccess({ resp })),
          catchError((error: unknown) => of(NotificationFeedActions.loadMoreNotificationsFailure({ error })))
        )
      )
    )
  );

  /**
   * More pages are only asked for on scroll, and a page whose items were all filtered out (see
   * `NotificationFeedService`) renders nothing to scroll past, so fetch the next one straight away.
   */
  loadPastEmptyPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.getUserNotificationsSuccess, NotificationFeedActions.loadMoreNotificationsSuccess),
      filter(({ resp }) => resp.hasMore && resp.notifications.length === 0),
      map(() => NotificationFeedActions.loadMoreNotifications())
    )
  );

  loadMoreNotificationsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.loadMoreNotificationsFailure),
      map(({ error }) => NotificationActions.notifyError({ error, message: 'Failed to load more notifications' }))
    )
  );

  logLoadMoreNotificationsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.loadMoreNotificationsFailure),
      map(({ error }) =>
        LoggingActions.error({
          log: {
            message: 'Failed to load more notifications',
            error,
          },
        })
      )
    )
  );

  /** Poll for notifications every 10 minutes to check for new ones and update unread status indicator.
   * Starts on app load and resets when the feed is closed. Each tab's timer starts at its own load time,
   * so polls stay spread out across clients. */
  pollNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.fetchTier2Data, NotificationFeedActions.init, NotificationFeedActions.close),
      concatLatestFrom(() => this.store.select(selectNotificationsEnabled)),
      filter(([, enabled]) => !!enabled),
      switchMap(() => interval(600000).pipe(map(() => NotificationFeedActions.checkForNewUnreadNotifications())))
    )
  );

  checkForNewUnreadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.checkForNewUnreadNotifications, OneSignalActions.notificationDisplayed),
      concatLatestFrom(() => this.store.select(NotificationFeedSelectors.selectShowUnreadStatusIndicator)),
      /** if the unread status indicator is already visible or the feed is open, don't poll */
      filter(([_, badgeIsVisible]) => {
        const isFeedOpen = this.router.url.includes('(detail:detail/notifications)');
        return !badgeIsVisible && !isFeedOpen;
      }),
      switchMap(() =>
        this.notificationFeedService.getNotificationsInit().pipe(
          concatLatestFrom(() => this.store.select(selectCurrentUser)),
          map(([resp, user]) => {
            const unreadStatusIndicator = this.computeUnreadStatusIndicator(resp.notifications, user);
            return NotificationFeedActions.updateUnreadStatusIndicator({ unreadStatusIndicator });
          }),
          /** silently fail */
          catchError(() => EMPTY)
        )
      )
    )
  );

  openNotificationsFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      map(() => {
        return DetailViewActions.opened({
          config: { pathParts: [DetailType.notifications] },
          source: 'Notifications Detail',
        });
      })
    )
  );

  closeNotificationFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.close),
      map(() => DetailViewActions.close())
    )
  );

  updateUserLastOpenedNotificationFeedAt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      concatLatestFrom(() => this.store.select(selectCurrentUserId)),
      map(([, userId]) =>
        UsersStateActions.updateQuietly({
          userId,
          update: { lastOpenedNotificationFeedAt: new Date() },
        })
      )
    )
  );

  toggleNotificationReadStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.toggleNotificationReadStatus),
      map(({ notification }) =>
        !!notification.markedReadAt
          ? NotificationFeedActions.markNotificationAsUnread({ id: notification.id })
          : NotificationFeedActions.markNotificationsAsRead({ ids: [notification.id] })
      )
    )
  );

  markNotificationsAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markNotificationsAsRead),
        switchMap(({ ids }) => this.notificationFeedService.markNotificationsAsRead(ids))
      ),
    { dispatch: false }
  );

  markNotificationAsUnread$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markNotificationAsUnread),
        switchMap(({ id }) => this.notificationFeedService.markNotificationAsUnread(id))
      ),
    { dispatch: false }
  );

  markAllAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markAllAsRead),
        switchMap(() => this.notificationFeedService.markAllAsRead())
      ),
    { dispatch: false }
  );

  /**
   * When a notification item is selected, navigate to the relevant page, set the team, and open the detail view.
   *
   * Flow:
   * 1. Navigate to the page if not already there (router.navigate returns null if already on route)
   * 2. Change the team if not already selected (wait for getSelectedTeamSuccess if team changes)
   * 3. Open the detail view in the feed's place; a type without one (a KPI) closes the feed instead,
   *    since the panel is a named outlet that otherwise stays open over the page
   */
  openNotificationItemDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.openItemDetail),
      filter(({ notification }) => notification.itemType !== NotificationItemType.milestone),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
      switchMap(([{ notification }, currentTeamId]) => {
        const pageRoute = itemTypeToPageRoute[notification.itemType];
        const detailType = itemTypeToDetailType[notification.itemType];

        if (!pageRoute) return EMPTY;

        const needsTeamChange = currentTeamId !== notification.teamId;
        const changeTeamAction = TeamListStateActions.changeFilterBarTeam({ id: notification.teamId });

        /** What follows landing on the page: the team switch, then the detail view if the item has one */
        const afterNavigation$ = () => {
          const closeFeedAction = NotificationFeedActions.close();
          if (!detailType) return needsTeamChange ? of(changeTeamAction, closeFeedAction) : of(closeFeedAction);

          const openDetailAction = DetailViewActions.opened({
            config: { pathParts: [detailType, notification.itemId] },
            source: 'Notification Item Selected',
          });

          if (!needsTeamChange) return of(openDetailAction);

          return concat(
            of(changeTeamAction),
            this.actions$.pipe(
              ofType(TeamListStateActions.getSelectedTeamSuccess),
              filter(({ team }) => team?._id === notification.teamId),
              take(1),
              map(() => openDetailAction)
            )
          );
        };

        /** If already on route, skip navigation */
        if (this.router.url.includes(pageRoute)) return afterNavigation$();

        return from(this.router.navigate([pageRoute])).pipe(
          concatMap(success => (success === false ? EMPTY : afterNavigation$()))
        );
      })
    )
  );

  /**
   * Handle milestone notifications differently - navigate to the individual rock page with milestone detail
   *
   * Flow:
   * 1. Navigate to /rocks/{rockId} if not already there
   * 2. Open the milestone detail view
   */
  openNotificationMilestoneDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.openItemDetail),
      filter(({ notification }) => notification.itemType === NotificationItemType.milestone),
      switchMap(({ notification }) => {
        if (!notification.rockId) return EMPTY;

        const openDetailAction = DetailViewActions.opened({
          config: { pathParts: [DetailType.milestone, notification.itemId] },
          source: 'Notification Item Selected',
        });

        const targetRoute = `/rocks/${notification.rockId}`;
        const isAlreadyOnRoute = this.router.url.includes(targetRoute);

        /** If already on the rock page, skip navigation */
        if (isAlreadyOnRoute) return of(openDetailAction);

        /** Navigate to the rocks page, then open milestone detail */
        return from(this.router.navigate([targetRoute])).pipe(
          concatMap(success => {
            if (success === false) return EMPTY;
            return of(openDetailAction);
          })
        );
      })
    )
  );

  markItemAsReadAfterOpeningItemDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.openItemDetail),
        switchMap(({ notification }) => this.notificationFeedService.markNotificationAsOpened(notification.id))
      ),
    { dispatch: false }
  );

  private computeUnreadStatusIndicator(
    notifications: NinetyNotification[],
    user: UserModelState | null | undefined
  ): boolean {
    // Don't recalculate badge if the feed is currently open - prevents race condition
    const isFeedOpen = this.router.url.includes('(detail:detail/notifications)');

    if (isFeedOpen || !user || !notifications?.length) return false;

    // If the user has never opened the feed, return true if there are any unread notifications
    if (!user?.lastOpenedNotificationFeedAt) return notifications.some(notification => !notification.markedReadAt);

    const lastOpenedDate = new Date(user.lastOpenedNotificationFeedAt);

    return notifications.some(
      notification => !notification.markedReadAt && new Date(notification.createdAt) > lastOpenedDate
    );
  }
}
